import { useState, useEffect } from "react";
import { View, ScrollView, Platform, Linking, TouchableOpacity } from "react-native";
import { Image } from "@/components/ui/Image";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Pill } from "@/components/ui/Pill";
import { useI18n } from "@/context/I18nContext";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import type { SupportedLocale } from "@chinese-vendor/i18n";
import Logo from "@assets/logo.svg";
import LogoWithWordMark from "@assets/logo-with-wordmark.svg";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";

// ─── Assets (web: larger crops, mobile: smaller crops from Figma) ────────────

const WEB = {
  blobTop: require("@assets/images/vendor-landing/blob-top.png"),
  blobBottomLeft: require("@assets/images/vendor-landing/blob-bottom-left.png"),
  blobBottomRight: require("@assets/images/vendor-landing/blob-bottom-right.png"),
} as const;

const MOBILE = {
  blobTop: require("@assets/images/vendor-landing/mobile-blob-top.png"),
  blobBottomLeft: require("@assets/images/vendor-landing/mobile-blob-bottom-left.png"),
  blobBottomRight: require("@assets/images/vendor-landing/mobile-blob-bottom-right.png"),
} as const;

const isWeb = Platform.OS === "web";

type Step = "language-select" | "landing";

/**
 * Vendor Landing Page — two steps:
 *   1. Language selection (first visit only, persisted to SecureStore)
 *   2. Main landing content (current GetStarted design)
 * Figma language-select: node 12536:4761
 * Figma landing web:     node 12029:26045 (1440px)
 * Figma landing mobile:  node 12027:38618 (414px)
 */
export default function GetStartedScreen() {
  const { t, isRTL, locale, setLocale, availableLocales, localeHydrated, hasStoredLocale } =
    useI18n();
  const router = useRouter();
  const { top, bottom } = useSafeAreaInsets();

  const [step, setStep] = useState<Step>("language-select");
  const [selectedLocale, setSelectedLocale] = useState<SupportedLocale>(locale);

  // If the vendor has already saved a locale preference, skip straight to landing
  useEffect(() => {
    if (localeHydrated && hasStoredLocale) setStep("landing");
  }, [localeHydrated, hasStoredLocale]);

  const handleSaveLanguage = () => {
    setLocale(selectedLocale);
    setStep("landing");
  };

  const handleGetStarted = () => {
    router.push("/(auth)/register");
  };

  const assets = isWeb ? WEB : MOBILE;

  // ── Image triangle layout values ────────────────────────────────────────────
  // Web:    top 280×280 ml=138 | bl 240×240 mt=200 | br 200×200 ml=278 mt=248
  // Mobile: top 200×200 ml=58  | bl 160×160 mt=170 | br 120×120 ml=158 mt=198
  const img = isWeb
    ? {
        topSize: 260,
        blSize: 220,
        brSize: 200,
        topLeft: 138,
        blTop: 220,
        brLeft: 278,
        brTop: 248,
        containerW: 478,
        containerH: 448,
      }
    : {
        topSize: 180,
        blSize: 140,
        brSize: 120,
        topLeft: 58,
        blTop: 170,
        brLeft: 158,
        brTop: 198,
        containerW: 278,
        containerH: 320,
      };

  return (
    <LinearGradient
      colors={["#C5D9FF", "#FFFFFF", "#FFCCD8"]}
      locations={[0, 0.45, 1]}
      style={{ flex: 1, paddingTop: top, paddingBottom: bottom }}
    >
      {step === "language-select" ? (
        // ── Step 1: Language Selection ─────────────────────────────────────────
        <ScrollView
          contentContainerStyle={{ flexGrow: 1, justifyContent: "center" }}
          showsVerticalScrollIndicator={false}
        >
          <View
            className={`self-center w-full tablet:max-w-3xl ${isWeb ? "px-page-x py-10" : "px-6 py-12"}`}
          >
            {/* Logo + welcome text */}
            <View className="items-center mb-14">
              <Logo accessibilityLabel="AladdinMiles" />
              <View className="items-center mt-4 gap-1">
                <AppText className="text-dark-text-50 text-lg text-center" weight="medium">
                  {t("vendor.languageSelect.welcomeTo")}
                </AppText>
                <AppText className="text-dark-text-50 text-2xl text-center" weight="semibold">
                  {t("vendor.languageSelect.appName")}
                </AppText>
              </View>
            </View>

            {/* Prompt */}
            <AppText className="text-dark-text-50 text-lg text-center w-full mb-3" weight="medium">
              {t("vendor.languageSelect.title")}
            </AppText>

            {/* Language options card */}
            <View
              className="bg-white border border-stroke-50 rounded-lg w-full mb-6"
              style={{ overflow: "hidden" }}
            >
              {availableLocales.map((item, index) => (
                <View key={item.locale}>
                  {index > 0 && <View className="h-px bg-stroke-50" />}
                  <TouchableOpacity
                    onPress={() => setSelectedLocale(item.locale)}
                    activeOpacity={0.7}
                    accessibilityRole="radio"
                    accessibilityState={{ checked: selectedLocale === item.locale }}
                    accessibilityLabel={item.label}
                    className="flex-row items-center justify-between px-4 py-4"
                  >
                    <AppText
                      weight="medium"
                      className={cn(
                        "text-base",
                        selectedLocale === item.locale
                          ? "text-dark-text-50"
                          : "text-supporting-text"
                      )}
                    >
                      {item.label}
                    </AppText>
                    <RadioDot selected={selectedLocale === item.locale} />
                  </TouchableOpacity>
                </View>
              ))}
            </View>

            {/* Save Preference button */}
            <Button
              label="vendor.languageSelect.savePreference"
              onPress={handleSaveLanguage}
              className="w-full"
            />
          </View>
        </ScrollView>
      ) : (
        // ── Step 2: Landing content ────────────────────────────────────────────
        <>
          {/* ── Header ────────────────────────────────────────────────────────────────── */}
          <View
            className={`bg-transparent flex-row items-center justify-between ${isWeb ? "min-h-[70px] px-page-x border-b border-stroke-50 bg-white" : "min-h-[69px] px-4"}`}
            style={isRTL ? { flexDirection: "row-reverse" } : undefined}
          >
            <LogoWithWordMark accessibilityLabel="AladdinMiles" />

            {/* Change Language pill — returns to language selection step */}
            <Pill
              label="vendor.landing.changeLanguage"
              onPress={() => setStep("language-select")}
              icon={<Icon as={Feather} name="globe" size={16} className="text-blue-50" />}
            />
          </View>

          {/* ── Page content ───────────────────────────────────────────────────── */}
          <ScrollView contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
            <View
              className={`items-center ${isWeb ? "px-6 py-10" : "px-10 py-16"}`}
              style={
                isWeb
                  ? { maxWidth: 720, marginLeft: "auto", marginRight: "auto", width: "100%" }
                  : undefined
              }
            >
              {/* ── Blob images (triangle layout) ──────────────────────────────── */}
              <View
                style={{
                  width: img.containerW,
                  height: img.containerH,
                  marginBottom: isWeb ? 48 : 40,
                }}
              >
                {/* Top-center */}
                <View
                  style={{
                    position: "absolute",
                    top: 0,
                    left: img.topLeft,
                    width: img.topSize,
                    height: img.topSize,
                  }}
                >
                  <Image
                    source={assets.blobTop}
                    style={{ width: "100%", height: "100%" }}
                    contentFit="cover"
                  />
                </View>
                {/* Bottom-left */}
                <View
                  style={{
                    position: "absolute",
                    top: img.blTop,
                    left: 0,
                    width: img.blSize,
                    height: img.blSize,
                  }}
                >
                  <Image
                    source={assets.blobBottomLeft}
                    style={{ width: "100%", height: "100%" }}
                    contentFit="cover"
                  />
                </View>
                {/* Bottom-right */}
                <View
                  style={{
                    position: "absolute",
                    top: img.brTop,
                    left: img.brLeft,
                    width: img.brSize,
                    height: img.brSize,
                  }}
                >
                  <Image
                    source={assets.blobBottomRight}
                    style={{ width: "100%", height: "100%" }}
                    contentFit="cover"
                  />
                </View>
              </View>

              {/* ── Text block ─────────────────────────────────────────────────── */}
              <View className={`items-center w-full ${isWeb ? "gap-4 mb-12" : "gap-3 mb-10"}`}>
                <AppText
                  className={`text-dark-text-50 text-center w-full ${isWeb ? "text-hero-lg" : "text-hero"}`}
                  weight="bold"
                >
                  {t("vendor.landing.title")}
                </AppText>

                <AppText
                  className={`text-supporting-text text-center w-full ${isWeb ? "text-subhero-lg" : "text-subhero"}`}
                  weight="medium"
                >
                  {t("vendor.landing.subtitle")}
                </AppText>
              </View>

              {/* ── CTA Button ─────────────────────────────────────────────────── */}
              <Button label="vendor.landing.getStarted" onPress={handleGetStarted} />

              {/* ── Need Help ──────────────────────────────────────────────────── */}
              <View
                className="flex-row items-center justify-center gap-1 mt-8"
                style={isRTL ? { flexDirection: "row-reverse" } : undefined}
              >
                <AppText className="text-supporting-text text-sm" weight="medium">
                  {t("vendor.landing.needHelp")}
                </AppText>
                <AppText
                  className="text-blue-50 text-sm"
                  weight="medium"
                  onPress={() => Linking.openURL("mailto:support@aladdinmiles.com")}
                >
                  {t("vendor.landing.contactSupport")}
                </AppText>
              </View>
            </View>
          </ScrollView>
        </>
      )}
    </LinearGradient>
  );
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <View
      style={{
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: selected ? "#FF1654" : "#E4E7ED",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {selected && (
        <View
          style={{
            width: 12,
            height: 12,
            borderRadius: 6,
            backgroundColor: "#FF1654",
          }}
        />
      )}
    </View>
  );
}
